import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, DownloadIcon, BriefcaseIcon, GraduationCapIcon, AwardIcon, StarIcon, CheckCircleIcon, ShieldCheckIcon } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────────────────────
interface ExperienceItem {
  title: string;
  company: string;
  period: string;
  bullets: string[];
}
interface EducationItem {
  school: string;
  degree: string;
  detail?: string;
  year: string;
}
// ─── Data ────────────────────────────────────────────────────────────────────
const experience: ExperienceItem[] = [{
  title: 'Senior Product Manager',
  company: 'Executive Office of the President / Office of Administration IT Branch',
  period: 'Aug 2022 – Feb 2026',
  bullets: ['Led strategic modernization initiatives for mission-critical enterprise systems supporting more than 15 White House components, including Salesforce platform implementations, integrations, and UX enhancements driven through Figma-based design workflows and scalable component-driven design systems.', 'Served as the primary business interface between executive stakeholders and IT development teams within Agile frameworks, defining, documenting, and prioritizing business requirements, user stories, workflows, acceptance criteria, and detailed design specifications for product increments and sprints.', 'Led product management for the Eagle Human Capital Management MVP and Exit-Ready Offboarding applications, incorporating Salesforce-based process enhancements, reusable UI components, and enterprise design standards to streamline HR and operational workflows.', 'Facilitated cross-component coordination to ensure alignment with institutional policy, governance standards, security protocols, and accessibility compliance while optimizing Salesforce data architecture, automation capabilities, and enterprise-wide UI consistency.', 'Directed usability validation, journey mapping, process redesign, and enterprise design standardization leveraging Figma libraries and component frameworks to improve clarity, trust, scalability, and system adoption across secure platforms.', 'Supported and integrated AI, generative AI, and machine learning applications into automation workflows, analytics, and decision-support tools within secure federal environments to enhance operational efficiency, predictive insights, and user-centered experience delivery.']
}, {
  title: 'Senior UX & UI Designer',
  company: 'Koniag Government Services / U.S. Dept. of the Interior – BTFA',
  period: 'Oct 2023 – Jul 2025',
  bullets: ['Led digital modernization initiatives supporting AI-enabled federal systems and enterprise data platforms, integrating AI, generative AI (GAI), and machine learning (ML/AI) capabilities to enhance automation, analytics, and decision-support outcomes.', 'Conducted requirements validation sessions and facilitated stakeholder alignment workshops, translating business needs into user stories and managing delivery workflows through Jira, Azure DevOps, Wrike, and Asana within Agile frameworks.', 'Designed scalable, 508-compliant system interfaces aligned with WCAG and ADA accessibility standards, improving data accessibility, usability, and executive decision-support capabilities.', 'Developed and maintained enterprise Product Design Systems leveraging IBM Carbon and Material UI, producing high-fidelity prototypes and reusable component libraries to support iterative Agile delivery cycles.', 'Streamlined data reporting and visualization workflows across distributed federal teams while reinforcing governance, security, and enterprise UI consistency.']
}, {
  title: 'UX Product Manager',
  company: "SalonCentric (L'Oréal)",
  period: 'May 2023 – Oct 2023',
  bullets: ['Supported defense contracts and enterprise proposal development under strict security protocols, integrating AI, generative AI (GAI), and machine learning (ML/AI) tools to enhance research, workflow automation, and proposal analytics.', 'Delivered fully compliant documentation and publishing support for multi-million-dollar bids, ensuring adherence to Section 508, WCAG, and ADA accessibility standards.', 'Coordinated branding and communication initiatives aligned with enterprise standards while managing cross-functional workflows through Jira, Azure DevOps, Wrike, and Asana within Agile delivery environments.', 'Modernized web and communication systems by implementing scalable design frameworks, including IBM Carbon and Material UI–based Product Design Systems, to improve stakeholder engagement, governance alignment, and operational efficiency.']
}, {
  title: 'Product UX/UI Manager',
  company: 'ItWorks Global',
  period: 'Dec 2022 – May 2023',
  bullets: ['Improved checkout usability using Figma and Adobe Creative Cloud (including Adobe XD, Illustrator, and Photoshop), delivering brand-consistent digital experiences across international markets.', 'Directed usability testing, user-flow redesigns, and development of reusable brand asset libraries and scalable design system components to support rapid deployment and adoption.', 'Reduced cart abandonment by 27% and strengthened sales and representative enablement across 10+ countries through data-informed UX strategy and iterative optimization.', 'Led UI/UX teams through Material Design system enhancements and cross-platform interface standardization to ensure global brand consistency, accessibility alignment, and performance scalability.']
}, {
  title: 'Director of User Experience',
  company: 'Ashley Global Retail – Ashley Furniture Industries',
  period: 'Feb 2022 – Nov 2022',
  bullets: ['Led enterprise customer experience transformation initiatives across web, mobile, and in-store platforms serving over 100 million annual users, implementing scalable Product Design Systems leveraging IBM Carbon and Material UI to drive consistency and efficiency.', 'Coordinated cross-functional stakeholders across IT, marketing, merchandising, and operations while embedding Agile delivery practices and enterprise governance standards.', 'Reduced bounce rate by 40% and increased online revenue by 15% through structured journey analysis, accessibility-aligned interface improvements, and system optimization in compliance with Section 508, WCAG, and ADA standards.', 'Directed usability testing and implemented data-informed optimization strategies, reinforcing reusable component libraries, accessibility best practices, and scalable digital experience frameworks.']
}, {
  title: 'Brand Manager',
  company: 'Revealing Truth Incorporated',
  period: 'Apr 2018 – Feb 2022',
  bullets: ['Executed a unified rebrand across five divisions, resulting in a 60% increase in engagement.', 'Established an internal creative services division that generated a 15% increase in revenue.', 'Led organization-wide rebranding efforts, including visual identity and UX/UI design, improving message alignment and community engagement across digital and physical channels.', 'Established and launched a Mass Communications function, developing brand guidelines and leading storytelling initiatives to strengthen narrative coherence and visual impact across online, offline, and large-format media.']
}, {
  title: 'Sr Lead UX/UI Designer (Contract)',
  company: 'Tech Data APAC',
  period: 'Oct 2017 – May 2018',
  bullets: ['Enhanced usability for international digital products, utilizing Adobe XD and Sketch to design localized user flows for React-based implementation.', 'Collaborated with cross-functional stakeholders to design UIs that aligned Product and UX strategy with enterprise business goals and market-entry objectives.', 'Led user research and ideation efforts, developing personas, journey maps, and wireframe-to-high-fidelity designs using Adobe XD and Sketch, enabling localized and accessible user flows.', 'Co-developed a data-informed UX strategy and supporting documentation to ensure scalable, culturally relevant platform adoption across new markets.']
}, {
  title: 'Brand & Marketing Consultant',
  company: 'Revealing Truth Incorporated',
  period: 'Jan 2017 – Oct 2017',
  bullets: ['Led a unified rebrand and strategic realignment of the creative organization, guiding teams through brand journey mapping to unlock and monetize internal creative capabilities.', 'Established an internal creative services division, strengthening brand consistency and generating a 15% increase in revenue through scalable, revenue-producing capabilities.', 'Achieved a 19% increase in lead conversions through a mobile-first website overhaul and digital brand refresh.']
}, {
  title: 'Marketing Manager',
  company: 'ACHQ Finance',
  period: 'Nov 2015 – Dec 2016',
  bullets: ['Led B2C digital transformation and marketing modernization, aligning product strategy, UX/UI design, and online engagement to business objectives.', 'Oversaw corporate identity redesign and customer acquisition strategies, including website and omnichannel touchpoints.', 'Executed a mobile-first website overhaul and refreshed digital branding across channels to enhance user experience and engagement.']
}, {
  title: 'Marketing Manager (TS/SCI Clearance)',
  company: 'ARMA Global Corporation – GDIT',
  period: 'Jun 2014 – Nov 2015',
  bullets: ['Managed B2B and B2C brand and marketing assets, aligning with enterprise standards to deliver consistent branding, marketing, design, and publishing support for high-stakes defense contracts and multi-million-dollar bids.', 'Directed corporate rebrand, internal communications, and external marketing initiatives for high-stakes contracts across defense and government clients.', 'Produced multi-channel campaigns, modernized web UX, and developed proposal designs for multi-million-dollar bids and brand awareness initiatives.', 'Delivered compliant, secure creative solutions under strict protocols, improving stakeholder engagement, operational efficiency, and integration into the broader enterprise ecosystem.']
}];
const education: EducationItem[] = [{
  school: 'Remington College',
  degree: 'Bachelor of Science — Digital Communications & Media/Multimedia',
  detail: 'Summa Cum Laude',
  year: '2006'
}, {
  school: 'University of Maryland Global Campus',
  degree: 'Non-Degree — Computer Information Science',
  year: '2009'
}, {
  school: 'University of Maryland Global Campus',
  degree: 'Non-Degree — Marketing & Communications',
  year: '2002'
}];
const certifications = ['Adobe Creative Community Advisor', 'Market Research Conjoint Analysis: Packaging and Product Bundling', 'Leading with Emotional Intelligence', 'Product Management: Product Strategy & Roadmapping', 'Geographic Information Systems Professional (GISP)', 'Product Manager Communications', 'Product & Data Storytelling', 'Information Security Management Systems'];
const awards = ['Platinum DotComm Award: Branding & Marketing Excellence', 'Joint Operations Achievement Award: US Special Operations Command / CACI', 'USCENTCOM Meritorious Award: Trans-Regional Web Initiative', 'US Army Accommodation Medal: Personnel Management & Leadership'];
const skillGroups = [{
  label: 'Creative & Brand',
  skills: ['Creative Direction', 'Art Direction', 'Graphic Design', 'Visual Design', 'Brand Strategy', 'Brand Identity Systems', 'Brand Architecture', 'Brand Positioning', 'Creative Strategy', 'Concept Development', 'Integrated Marketing Campaigns']
}, {
  label: 'UX / Product Design',
  skills: ['UX Design', 'UI Design', 'Product Design', 'Experience Design (XD)', 'Human-Centered Design', 'Interaction Design', 'Information Architecture', 'Wireframing', 'Prototyping', 'Usability Testing', 'Accessibility (WCAG)', 'Omnichannel Experience Design', 'Customer Journey Mapping', 'Design Systems']
}, {
  label: 'Strategy & Analytics',
  skills: ['Conversion Optimization', 'A/B Creative Testing', 'Data-Informed Design', 'Marketing Analytics', 'Performance Marketing Creative', 'Demand Generation Design', 'Content Marketing', 'Social Media Creative', 'Email Marketing Design']
}, {
  label: 'Leadership & Operations',
  skills: ['Cross-Functional Leadership', 'Creative Operations', 'Design Governance', 'Agency & Vendor Management', 'Collaboration', 'Design System Governance']
}, {
  label: 'Tools & Platforms',
  skills: ['Adobe Creative Cloud', 'Figma', 'AI-Assisted Creative Tools', 'Jira', 'Azure DevOps', 'Wrike', 'Asana', 'IBM Carbon', 'Material UI', 'Salesforce', 'React', 'Sketch']
}];
// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({
  icon: Icon,
  title
}: {
  icon: React.ElementType;
  title: string;
}) {
  return <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#B08D57]" data-id="element-646">
      <div className="w-8 h-8 rounded-lg bg-[#1C1F23] flex items-center justify-center flex-shrink-0" data-id="element-647">
        <Icon className="w-4 h-4 text-[#B08D57]" aria-hidden="true" data-id="element-648" />
      </div>
      <h2 className="font-heading font-bold text-[#1C1F23] text-xl lg:text-2xl tracking-tight" data-id="element-649">
        {title}
      </h2>
    </div>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function ResumePage() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: i * 0.07,
        ease: 'easeOut'
      }
    })
  };
  return <div className="min-h-screen bg-[#F7F7F5]" data-id="element-650">
      {/* Top bar */}
      <header className="bg-[#1C1F23] sticky top-0 z-40 shadow-lg" role="banner" data-id="element-651">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-652">
          <Link to="/" className="inline-flex items-center gap-2 text-[#C4D4DC] hover:text-[#B08D57] transition-colors text-sm font-body font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" aria-label="Back to portfolio" data-id="element-653">
            <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" data-id="element-654" />
            Back to Portfolio
          </Link>
          <span className="font-heading font-bold text-[#F7F7F5] text-base tracking-tight hidden sm:block" data-id="element-655">
            Michael K. <span className="text-[#B08D57]" data-id="element-656">Bigos</span>
          </span>
          <a href="/Michael_K_Bigos_Resume.pdf" download="Michael_K_Bigos_Resume.pdf" className="inline-flex items-center gap-2 bg-[#B08D57] hover:bg-[#9A7A48] text-[#1C1F23] text-xs font-body font-bold px-4 py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1F23]" aria-label="Download resume PDF" data-id="element-657">
            <DownloadIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-658" />
            Download PDF
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" id="resume-content" data-id="element-659">
        {/* Resume Header */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="mb-12" data-id="element-660">
          <div className="bg-[#1C1F23] rounded-2xl px-8 lg:px-12 py-10 lg:py-12" data-id="element-661">
            <p className="text-[#B08D57] text-xs font-body font-semibold tracking-[0.25em] uppercase mb-3" data-id="element-662">
              Curriculum Vitae
            </p>
            <h1 className="font-heading font-bold text-[#F7F7F5] text-4xl lg:text-5xl mb-2 tracking-tight" data-id="element-663">
              Michael K. Bigos Jr.
            </h1>
            <p className="font-body text-[#ADBEC8] text-lg mb-6" data-id="element-664">
              Senior Product Designer · UX/UI Leader · Brand Strategist
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-665">
              {['20+ Years Experience', 'U.S. Army Veteran', 'Section 508 · WCAG · ADA', 'Federal & Enterprise', 'TS/SCI Clearance Eligible'].map(tag => <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full bg-[#B08D57]/15 border border-[#B08D57]/30 text-[#B08D57] text-xs font-body font-semibold tracking-wide" data-id="element-666">
                  {tag}
                </span>)}
            </div>
          </div>
        </motion.div>

        {/* Summary */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="mb-12" aria-labelledby="summary-heading" data-id="element-667">
          <SectionHeader icon={StarIcon} title="Summary" data-id="element-668" />
          <p className="font-body text-[#2C3A44] text-base leading-relaxed" data-id="element-669">
            Senior Product Designer with over{' '}
            <strong data-id="element-670">20 years of digital experience</strong> and more than{' '}
            <strong data-id="element-671">
              15 years leading product, UX/UI, and cross-functional teams
            </strong>{' '}
            within complex enterprise environments. I have led 0–1 product
            lifecycles from discovery and vision through strategy, scalable
            design systems, and go-to-market execution, transforming ambiguity
            into high-impact, revenue-generating solutions. I bring extensive
            expertise in <strong data-id="element-672">Adobe Creative Suite and Figma</strong>, along
            with deep experience building, scaling, and governing design systems
            that improve consistency, efficiency, and cross-functional alignment
            while ensuring compliance with{' '}
            <strong data-id="element-673">Section 508, WCAG, and ADA</strong> accessibility standards.
            I am highly proficient in collaborative project management and
            delivery workflows using{' '}
            <strong data-id="element-674">Jira, Azure DevOps, Wrike, and Asana</strong> to align
            stakeholders, manage roadmaps, and ensure on-time execution. My
            background spans B2B/SaaS, health and wellness, and AI-enabled
            design, supported by strong omni-channel CX strategy and global
            brand positioning, and I consistently deliver cohesive, accessible,
            and differentiated experiences that drive adoption, engagement, and
            measurable business value.
          </p>
        </motion.section>

        {/* Experience */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="mb-12" aria-labelledby="experience-heading" data-id="element-675">
          <SectionHeader icon={BriefcaseIcon} title="Experience" data-id="element-676" />
          <div className="space-y-10" data-id="element-677">
            {experience.map((job, i) => <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-40px'
          }} className="relative pl-6 border-l-2 border-[#D8D8D4] hover:border-[#B08D57] transition-colors duration-300" data-id="element-678">
                {/* Timeline dot */}
                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#B08D57] ring-2 ring-[#F7F7F5]" aria-hidden="true" data-id="element-679" />
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-3" data-id="element-680">
                  <div data-id="element-681">
                    <h3 className="font-heading font-bold text-[#1C1F23] text-lg leading-snug" data-id="element-682">
                      {job.title}
                    </h3>
                    <p className="font-body text-[#B08D57] font-semibold text-sm" data-id="element-683">
                      {job.company}
                    </p>
                  </div>
                  <span className="font-body text-[#8FA4B0] text-xs font-medium whitespace-nowrap bg-[#E8E8E4] px-3 py-1 rounded-full self-start mt-0.5" data-id="element-684">
                    {job.period}
                  </span>
                </div>
                <ul className="space-y-2" role="list" data-id="element-685">
                  {job.bullets.map((bullet, j) => <li key={j} className="flex items-start gap-2.5" data-id="element-686">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B08D57] mt-2 flex-shrink-0" aria-hidden="true" data-id="element-687" />
                      <span className="font-body text-[#4A5A66] text-sm leading-relaxed" data-id="element-688">
                        {bullet}
                      </span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Education */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-12" aria-labelledby="education-heading" data-id="element-689">
          <SectionHeader icon={GraduationCapIcon} title="Education" data-id="element-690" />
          <div className="space-y-5" data-id="element-691">
            {education.map((edu, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 bg-white border border-[#E8E8E4] rounded-xl px-6 py-5" data-id="element-692">
                <div data-id="element-693">
                  <h3 className="font-heading font-semibold text-[#1C1F23] text-base" data-id="element-694">
                    {edu.school}
                  </h3>
                  <p className="font-body text-[#4A5A66] text-sm mt-0.5" data-id="element-695">
                    {edu.degree}
                  </p>
                  {edu.detail && <p className="font-body text-[#B08D57] text-xs font-semibold mt-1 uppercase tracking-wide" data-id="element-696">
                      {edu.detail}
                    </p>}
                </div>
                <span className="font-body text-[#8FA4B0] text-xs font-medium bg-[#E8E8E4] px-3 py-1 rounded-full self-start whitespace-nowrap" data-id="element-697">
                  {edu.year}
                </span>
              </div>)}
          </div>
        </motion.section>

        {/* Licenses & Certifications */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-12" aria-labelledby="certs-heading" data-id="element-698">
          <SectionHeader icon={ShieldCheckIcon} title="Licenses & Certifications" data-id="element-699" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-700">
            {certifications.map((cert, i) => <div key={i} className="flex items-start gap-3 bg-white border border-[#E8E8E4] rounded-lg px-4 py-3" data-id="element-701">
                <CheckCircleIcon className="w-4 h-4 text-[#B08D57] flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-702" />
                <span className="font-body text-[#2C3A44] text-sm leading-snug" data-id="element-703">
                  {cert}
                </span>
              </div>)}
          </div>
        </motion.section>

        {/* Skills */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-12" aria-labelledby="skills-heading" data-id="element-704">
          <SectionHeader icon={StarIcon} title="Skillset & Competencies" data-id="element-705" />
          <div className="space-y-6" data-id="element-706">
            {skillGroups.map((group, i) => <div key={i} data-id="element-707">
                <h3 className="font-heading font-semibold text-[#1C1F23] text-sm uppercase tracking-widest mb-3" data-id="element-708">
                  {group.label}
                </h3>
                <div className="flex flex-wrap gap-2" role="list" aria-label={`${group.label} skills`} data-id="element-709">
                  {group.skills.map(skill => <span key={skill} role="listitem" className="inline-flex items-center px-3 py-1 rounded-full bg-[#1C1F23] text-[#C4D4DC] text-xs font-body font-medium" data-id="element-710">
                      {skill}
                    </span>)}
                </div>
              </div>)}
          </div>
        </motion.section>

        {/* Honors & Awards */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-12" aria-labelledby="awards-heading" data-id="element-711">
          <SectionHeader icon={AwardIcon} title="Honors & Awards" data-id="element-712" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-713">
            {awards.map((award, i) => <div key={i} className="flex items-start gap-3 bg-[#1C1F23] border-l-4 border-[#B08D57] rounded-r-xl px-5 py-4" data-id="element-714">
                <AwardIcon className="w-4 h-4 text-[#B08D57] flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-715" />
                <span className="font-body text-[#C4D4DC] text-sm leading-snug" data-id="element-716">
                  {award}
                </span>
              </div>)}
          </div>
        </motion.section>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-[#1C1F23] rounded-2xl px-8 py-8" data-id="element-717">
          <div data-id="element-718">
            <p className="font-heading font-bold text-[#F7F7F5] text-lg mb-1" data-id="element-719">
              Ready to connect?
            </p>
            <p className="font-body text-[#ADBEC8] text-sm" data-id="element-720">
              Download the full resume or return to the portfolio to view case
              studies.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0" data-id="element-721">
            <a href="/Michael_K_Bigos_Resume.pdf" download="Michael_K_Bigos_Resume.pdf" className="inline-flex items-center justify-center gap-2 bg-[#B08D57] hover:bg-[#9A7A48] text-[#1C1F23] text-sm font-body font-bold px-5 py-2.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57]" data-id="element-722">
              <DownloadIcon className="w-4 h-4" aria-hidden="true" data-id="element-723" />
              Download Resume
            </a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border border-[#F7F7F5]/20 text-[#F7F7F5] hover:bg-[#F7F7F5]/10 text-sm font-body font-medium px-5 py-2.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57]" data-id="element-724">
              <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" data-id="element-725" />
              View Portfolio
            </Link>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1C1F23] border-t border-[#F7F7F5]/5 py-6 mt-12" role="contentinfo" data-id="element-726">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-727">
          <span className="font-heading font-bold text-[#F7F7F5] text-sm" data-id="element-728">
            Michael K. <span className="text-[#B08D57]" data-id="element-729">Bigos</span>
          </span>
          <span className="font-body text-[#F7F7F5]/30 text-xs" data-id="element-730">
            Section 508 · WCAG AA · ADA Compliant · ©{' '}
            {new Date().getFullYear()} Michael K. Bigos
          </span>
        </div>
      </footer>
    </div>;
}